import React, { useState } from 'react';
import config from '../config';
import { KB_ABOUT_CHANGE, KB_SAVE_RECORDS } from '../constants/kbLinks';
import FmhMigrationBanner from './FmhMigrationBanner';

const FMH_LOGO = '/assets/images/followmyhealth.svg';

// The tests mirror this date; update both sides together.
const RETIREMENT_DATE = 'Monday, December 7';

const HEADING = 'A better way to manage your health is here';
// Only the expanded state has a collapsible region to point at. Collapsed, the toggle
//   carries no aria-controls at all, which is better than an id that resolves to the
//   always-visible summary.
const CONTENT_ID = 'login-retirement-banner-content';

// A plain element rather than a component, so it stays part of this component's own
//   shallow-rendered tree. Both variants use it, but only one of them ever renders.
const aboutChangeLink = (
    <a
        className="login-retirement-banner__link"
        href={KB_ABOUT_CHANGE}
        rel="noopener noreferrer"
        target="_blank"
    >
        Read more about this change
    </a>
);

// Pre-cutover notice: three paragraphs, an orange call to action, then the article link.
const preRetirement = {
    summary: `Patient Fusion closes the week of ${RETIREMENT_DATE}`,
    body: (
        <>
            <p className="login-retirement-banner__paragraph">
                <b>Patient Fusion closes the week of {RETIREMENT_DATE}.</b> Your care stays the same. Only the
                website where you see your records is changing. Most providers are moving to
                FollowMyHealth, where you can see your records, message your care team, and manage
                visits in one place.
            </p>
            <p className="login-retirement-banner__paragraph">
                <b>Sign in before {RETIREMENT_DATE} and save a copy of your records.</b> If your
                provider moves to FollowMyHealth, you will get an email invitation. Your records
                will already be waiting there.
            </p>
            <p className="login-retirement-banner__paragraph">
                Your old messages do <b>not</b> move to FollowMyHealth. Save any you want to keep.
            </p>
            <div className="login-retirement-banner__actions">
                <a
                    className="btn--primary login-retirement-banner__cta"
                    href={KB_SAVE_RECORDS}
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    Learn how to save your records
                </a>
                {aboutChangeLink}
            </div>
        </>
    )
};

// Post-retirement: three paragraphs and the article link only, with no orange call to action.
const postRetirement = {
    summary: `Patient Fusion has closed. Here's how to get your records`,
    body: (
        <>
            <p className="login-retirement-banner__paragraph">
                <b>Patient Fusion is now closed.</b> Your care stays the same. Only the website
                where you see your records has changed.
            </p>
            <p className="login-retirement-banner__paragraph">
                Most providers now use FollowMyHealth. There, you can see your records, message your
                care team, and manage visits in one place. If your provider uses FollowMyHealth,
                check your email for your invitation and use it to connect to your records. If your
                provider does not use FollowMyHealth, call their office and ask for a copy of your
                records.
            </p>
            <p className="login-retirement-banner__paragraph">
                <b>Have a form to fill out?</b> Your provider may have sent you a form for an
                appointment you made before Patient Fusion closed. You can still sign in or sign up
                here to finish it. Your provider will tell you where to fill out new forms.
            </p>
            <div className="login-retirement-banner__actions">
                {aboutChangeLink}
            </div>
        </>
    )
};

const LoginRetirementBanner = () => {
    // Keep the hook above the config checks so the hook count is the same either way.
    const [isExpanded, setIsExpanded] = useState(true);

    // Post-retirement wins: once closure has been announced, a stale "closing soon"
    //   message must not override it.
    let notice = null;
    if (config.showLoginPostRetirementBanner) {
        notice = postRetirement;
    } else if (config.showLoginPreRetirementBanner) {
        notice = preRetirement;
    }

    // Neither flag is on, so the original FollowMyHealth migration banner stays as is.
    if (!notice) {
        return (
            <div className="inline-flex-group banner__wrapper">
                <FmhMigrationBanner />
            </div>
        );
    }

    const bannerClass = isExpanded
        ? 'login-retirement-banner'
        : 'login-retirement-banner login-retirement-banner--collapsed';

    return (
        <div
            aria-label="Patient Fusion retirement notice"
            className={bannerClass}
            role="region"
        >
            {/* The toggle sits outside the conditional at the same index in both states,
                so React reconciles it to the same element and keyboard focus survives a
                collapse. It is first rather than last so it precedes the two links in tab
                order; the stylesheet orders it back to the right edge. */}
            <button
                {...(isExpanded ? { 'aria-controls': CONTENT_ID } : {})}
                aria-expanded={isExpanded}
                className="btn--link login-retirement-banner__toggle"
                onClick={() => setIsExpanded(!isExpanded)}
                type="button"
            >
                {isExpanded ? 'Hide' : 'Read more'}
                <span aria-hidden="true">{isExpanded ? ' ▲' : ' ▼'}</span>
            </button>
            {isExpanded ? (
                <div className="login-retirement-banner__content" id={CONTENT_ID}>
                    <div className="login-retirement-banner__heading-row">
                        <img className="login-retirement-banner__logo" src={FMH_LOGO} alt="" />
                        <h2 className="login-retirement-banner__heading">{HEADING}</h2>
                    </div>
                    {notice.body}
                </div>
            ) : (
                <b className="login-retirement-banner__summary">
                    {notice.summary}
                </b>
            )}
        </div>
    );
};

export default LoginRetirementBanner;
